import React, { useState } from "react";
import { MainProfileCard } from "./main-profile-card";
import { ProfilesList } from "./profiles-list";
import { useProfile, useSecteurs } from "./hooks";
import { Spinner } from "./spinner";
import { CampaignProvider, useCampaign } from "./campaign-context";
import { SaveCampaignModal } from "./save-campaign-modal";
import { SavedCampaignsTable } from "./saved-campaigns-table/index";
import {
  CandidatDrawer,
  EnseigneDrawer,
  VilleDrawer,
  LocationDrawer,
} from "../../profile_utils";

interface Profile {
  id: number;
  type: "candidat" | "enseigne" | "ville" | "location";
  name: string;
  avatar_url?: string;
  logo_url?: string;
  [key: string]: any;
}

function CampaignBuilderDashboardInner() {
  const {
    data: profile,
    isLoading: profileLoading,
    error: profileError,
  } = useProfile();
  const { data: secteurs, isLoading: secteursLoading } = useSecteurs();
  const {
    hasMinimumSelection,
    getFiltersForBlock,
    hasSelection,
    selectedEntity,
    setSelectedEntity,
  } = useCampaign();
  const [showModal, setShowModal] = useState(false);

  const loading = profileLoading || secteursLoading;
  const error = profileError;

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Spinner size="lg" variant="default" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="card p-8 text-center text-red-500">
        <p>Error: {(error as any)?.message || "Une erreur est survenue"}</p>
      </div>
    );
  }

  if (!profile || !profile.id) {
    return (
      <div className="card p-8 text-center text-slate-500">
        <p>Type de profil non supporté.</p>
      </div>
    );
  }

  // Determine which 3 other profile types to show
  const allTypes = ["enseignes", "candidats", "villes", "locations"];
  const currentType = `${profile.type}s`;
  const otherTypes = allTypes
    .filter((type) => type !== currentType)
    .slice(0, 3);

  return (
    <div>
      <div className="flex flex-col space-y-8">
        {/* 2x2 Grid */}
        <div className="card">
          {/* Top Left: Main Profile (Your Profile) */}
          <div className="grid grid-cols-2 divide-x divide-y divide-slate-400/60 [&>:nth-child(2)]:!border-t-0 [&>:nth-child(3)]:!border-l-0">
            <div>
              <MainProfileCard profile={profile} secteurs={secteurs || []} />
            </div>

            {/* Top Right: First Other Profile Type */}
            <div>
              <ProfilesList
                entityType={otherTypes[0] as any}
                filters={getFiltersForBlock(otherTypes[0])}
                hasSelection={hasSelection(otherTypes[0])}
              />
            </div>

            {/* Bottom Left: Second Other Profile Type */}
            <div>
              <ProfilesList
                entityType={otherTypes[1] as any}
                filters={getFiltersForBlock(otherTypes[1])}
                hasSelection={hasSelection(otherTypes[1])}
              />
            </div>

            {/* Bottom Right: Third Other Profile Type */}
            <div>
              <ProfilesList
                entityType={otherTypes[2] as any}
                filters={getFiltersForBlock(otherTypes[2])}
                hasSelection={hasSelection(otherTypes[2])}
              />
            </div>
          </div>
          <div className="border-t py-3 px-4">
            <button
              disabled={!hasMinimumSelection}
              className="bg-default-600 text-white rounded-lg w-full items-center justify-center py-3 text-lg disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={() => setShowModal(true)}
            >
              <span>Enregistrer ce projet</span>
            </button>
          </div>
        </div>

        {/* Saved Campaigns Table */}
        <SavedCampaignsTable />
      </div>

      {/* Save Campaign Modal */}
      {showModal && (
        <SaveCampaignModal
          onClose={() => setShowModal(false)}
          onSuccess={() => {
            setShowModal(false);
          }}
        />
      )}

      {/* Profile Drawers */}
      {selectedEntity && selectedEntity.type === "candidat" && (
        <CandidatDrawer
          candidat={selectedEntity}
          onClose={() => setSelectedEntity(null)}
        />
      )}
      {selectedEntity && selectedEntity.type === "enseigne" && (
        <EnseigneDrawer
          enseigne={selectedEntity}
          onClose={() => setSelectedEntity(null)}
        />
      )}
      {selectedEntity && selectedEntity.type === "ville" && (
        <VilleDrawer
          ville={selectedEntity}
          onClose={() => setSelectedEntity(null)}
        />
      )}
      {selectedEntity && selectedEntity.type === "location" && (
        <LocationDrawer
          location={selectedEntity}
          onClose={() => setSelectedEntity(null)}
        />
      )}
    </div>
  );
}

export function CampaignBuilderDashboard() {
  const { data: profile, isLoading } = useProfile();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Spinner size="lg" variant="default" />
      </div>
    );
  }

  if (!profile || !profile.id) {
    return (
      <div className="card p-8 text-center text-slate-500">
        <p>Type de profil non supporté.</p>
      </div>
    );
  }

  return (
    <CampaignProvider mainProfile={profile}>
      <CampaignBuilderDashboardInner />
    </CampaignProvider>
  );
}
